<script>
import {useCartStore} from "@js/store/CartStore.js";
import NumberFormatComponent from "@js/components/Formatter/NumberFormatComponent.vue";
import CallbackErrorMessage from "@js/components/Statuses/CallbackErrorMessage.vue";
import {IMaskDirective} from "vue-imask";
import PrivacyPolicyAgreementLink from "@js/components/Sections/Links/PrivacyPolicyAgreementLink.vue";
import {computed} from "vue";
import SpinnerIndicator from "@js/components/Statuses/SpinnerIndicator.vue";

export default {
    name: "CartForm",
    components: {
        SpinnerIndicator,
        PrivacyPolicyAgreementLink,
        CallbackErrorMessage,
        NumberFormatComponent,
    },
    directives: {imask: IMaskDirective},
    setup() {
        const cartStore = useCartStore();

        // форматтеры итогов
        const productsSum = computed(() => cartStore.subTotal);
        const weightTotal = computed(() => cartStore.weightTotal);

        const total = computed(() => productsSum.value);

        // маски
        const phoneMask = {mask: "+7 (000) 000-00-00"};
        const innMask = {mask: "000000000000"};

        const updatePhone = (val) => {
            cartStore.form.phone = val;
        };

        const updateInn = (val) => {
            cartStore.form.inn = val;
        };

        // удобный alias для состояния отправки
        const sending = computed(() => !!cartStore.loadings?.sending);

        return {
            cartStore,
            productsSum,
            weightTotal,
            total,
            phoneMask,
            updatePhone,
            innMask,
            updateInn,
            sending,
        };
    },
};
</script>

<template>
    <div class="flex flex-col bg-grayBg rounded-2xl p-4 gap-5">
        <!-- Итоги -->
        <div>
            <h2 class="text-lg font-medium mb-3">Стоимость заказа</h2>
            <div class="flex items-end justify-between w-full text-sm mb-2">
                <div class="font-medium text-amber-600">Стоимость доставки уточнит оператор</div>
            </div>
            <div class="flex items-end justify-between w-full text-sm mb-2">
                <div class="font-medium">Общий вес</div>
                <div class="font-bold">
                    <NumberFormatComponent :number="weightTotal" append_text=" кг"/>
                </div>
            </div>
            <div class="flex items-end justify-between w-full">
                <div class="font-medium">Итого к оплате</div>
                <div class="font-bold">
                    <NumberFormatComponent :number="total" append_text=" ₽"/>
                </div>
            </div>
        </div>

        <!-- Форма -->
        <div class="flex flex-col gap-3">
            <h2 class="text-lg font-medium">Оформление заказа</h2>

            <div>
                <input
                    type="text"
                    placeholder="Ф.И.О.*"
                    v-model="cartStore.form.full_name"
                    autocomplete="off"
                    class="block w-full px-0 py-2 text-gray-700 placeholder-gray-500 bg-transparent border-0 border-b-2 border-baseColor focus:outline-none focus:ring-0 focus:border-baseColor transition"
                    :class="[cartStore.form_errors && cartStore.form_errors['full_name'] ? 'border-red-600' : '']"
                />
                <CallbackErrorMessage :error="cartStore.form_errors" :error-msg="'full_name'"/>
            </div>

            <div>
                <input
                    type="tel"
                    :value="cartStore.form.phone"
                    v-imask="phoneMask"
                    @keyup="updatePhone($event.target.value)"
                    placeholder="+7 (___) ___-__-__*"
                    autocomplete="off"
                    class="block w-full px-0 py-2 text-gray-700 placeholder-gray-500 bg-transparent border-0 border-b-2 border-baseColor focus:outline-none focus:ring-0 focus:border-baseColor transition"
                    :class="[cartStore.form_errors && cartStore.form_errors['phone'] ? 'border-red-600' : '']"
                />
                <CallbackErrorMessage :error="cartStore.form_errors" :error-msg="'phone'"/>
            </div>

            <div>
                <input
                    type="email"
                    placeholder="Email"
                    v-model="cartStore.form.email"
                    autocomplete="off"
                    class="block w-full px-0 py-2 text-gray-700 placeholder-gray-500 bg-transparent border-0 border-b-2 border-baseColor focus:outline-none focus:ring-0 focus:border-baseColor transition"
                    :class="[cartStore.form_errors && cartStore.form_errors['email'] ? 'border-red-600' : '']"
                />
                <CallbackErrorMessage :error="cartStore.form_errors" :error-msg="'email'"/>
            </div>

            <div v-if="cartStore.form.payment_type === 'invoice'">
                <input
                    type="text"
                    :value="cartStore.form.inn"
                    v-imask="innMask"
                    @keyup="updateInn($event.target.value)"
                    placeholder="ИНН*"
                    autocomplete="off"
                    class="block w-full px-0 py-2 text-gray-700 placeholder-gray-500 bg-transparent border-0 border-b-2 border-baseColor focus:outline-none focus:ring-0 focus:border-baseColor transition"
                    :class="[cartStore.form_errors && cartStore.form_errors['inn'] ? 'border-red-600' : '']"
                />
                <CallbackErrorMessage :error="cartStore.form_errors" :error-msg="'inn'"/>
            </div>

            <div class="mt-2">
        <textarea
            rows="4"
            v-model="cartStore.form.comment"
            class="block p-2.5 w-full text-sm text-gray-900 bg-gray-50 rounded-lg border-2 border-gray-300 focus:ring-0 focus:border-baseColor"
            placeholder="Комментарий к заказу..."
        ></textarea>
            </div>

            <div class="mt-5">
                <PrivacyPolicyAgreementLink/>
            </div>

            <div>
                <button type="button"
                        @click.prevent="cartStore.sendForm" :disabled="sending"
                        class="flex items-center justify-center w-full gap-2 leading-tight uppercase text-nowrap bg-baseColor md:hover:bg-baseColor/80 text-white focus:outline-none focus:ring-0 font-bold rounded-xl text-sm px-5 py-4 transition disabled:opacity-60 disabled:cursor-not-allowed"
                        aria-live="polite">
                    <SpinnerIndicator v-if="sending" class="w-5 h-5"/>
                    {{ sending ? 'Отправляем…' : 'Оформить заказ' }}
                </button>
            </div>

            <!-- Сообщение об успехе (если нужно) -->
            <p
                v-if="cartStore.cart_form_successfully_sent"
                class="text-green-700 text-sm mt-1"
                role="status"
            >
                Заявка отправлена! Мы свяжемся с вами в ближайшее время.
            </p>
        </div>
    </div>
</template>

<style scoped lang="scss">
/* опираемся на утилиты Tailwind; дополнительных стилей не требуется */
</style>
